import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Plus, Edit, Pause, Play, Trash2, Eye, Users, 
  MapPin, Clock, DollarSign, Loader2, Briefcase, ArrowLeft, BarChart3, Lightbulb, Download, X,
  Target, ExternalLink, Mail, Linkedin, Github, CheckCircle, Award, Star, Sparkles, MessageCircleQuestion
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyJobsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { toast } = useToast();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsRemaining, setJobsRemaining] = useState(3);
  const [activeCount, setActiveCount] = useState(0);
  const [closedCount, setClosedCount] = useState(0);
  const [miReport, setMiReport] = useState(null);
  const [showMiModal, setShowMiModal] = useState(false);
  const [loadingMi, setLoadingMi] = useState(false);
  
  // Smart Sourcing state
  const [showSourcingModal, setShowSourcingModal] = useState(false);
  const [sourcingCandidates, setSourcingCandidates] = useState([]);
  const [loadingSourcing, setLoadingSourcing] = useState(false);
  const [sourcingJobTitle, setSourcingJobTitle] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchJobs();
  }, [isAuthenticated]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/my-jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allJobs = data.jobs || [];
        setJobs(allJobs);
        setActiveCount(data.active_count || 0);
        setJobsRemaining(data.jobs_remaining || 0);
        
        // Calculate closed jobs (paused, expired, deleted)
        const closed = allJobs.filter(job => 
          ['paused', 'expired', 'deleted'].includes(job.status)
        ).length;
        setClosedCount(closed);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to load jobs', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (jobId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Job paused successfully' });
        fetchJobs();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to pause job', variant: 'destructive' });
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Job deleted successfully' });
        fetchJobs();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete job', variant: 'destructive' });
    }
  };

  const handleViewMiReport = async (jobId, miReportStatus) => {
    if (miReportStatus === 'generating' || miReportStatus === 'pending') {
      toast({ 
        title: 'Report Generating', 
        description: 'Market Intelligence report is being generated. Please check back in a few moments.' 
      });
      return;
    }
    
    if (miReportStatus === 'failed') {
      toast({ 
        title: 'Generation Failed', 
        description: 'Report generation failed. Please try regenerating.',
        variant: 'destructive'
      });
      return;
    }
    
    setLoadingMi(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/mi-report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success && data.report) {
        // Open MI report in new tab
        window.open(`/mi-report/${data.report_id}`, '_blank');
        toast({ 
          title: '✅ Report Opened', 
          description: 'Market Intelligence report opened in a new tab.' 
        });
      } else {
        toast({ 
          title: 'Report Not Available', 
          description: data.message || 'Market Intelligence report is not available yet.' 
        });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to load MI report', 
        variant: 'destructive' 
      });
    } finally {
      setLoadingMi(false);
    }
  };

  const handleSmartSourcing = async (jobId, jobTitle) => {
    setLoadingSourcing(true);
    setSourcingJobTitle(jobTitle);
    try {
      const response = await fetch(`${BACKEND_URL}/api/smart-sourcing/source-for-job/${jobId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success && data.candidates) {
        setSourcingCandidates(data.candidates);
        setShowSourcingModal(true);
        toast({
          title: '✨ Candidates Found!',
          description: `Discovered ${data.total_found} matching candidates with contact details.`,
        });
      } else {
        toast({
          title: '❌ Sourcing Failed',
          description: 'Unable to find candidates at this time.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Smart sourcing error:', error);
      toast({
        title: '❌ Error',
        description: 'Failed to source candidates. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoadingSourcing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <Badge className="bg-green-100 text-green-700 border-green-300">Active</Badge>,
      draft: <Badge className="bg-gray-100 text-gray-700 border-gray-300">Draft</Badge>,
      paused: <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">Paused</Badge>,
      expired: <Badge className="bg-red-100 text-red-700 border-red-300">Expired</Badge>
    };
    return badges[status] || badges.draft;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Back to Home Button */}
        <div className="mb-4">
          <Button 
            onClick={() => navigate('/')} 
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">My Jobs</h1>
            <p className="text-slate-600 mt-1">
              Manage your job postings ({activeCount}/3 active)
            </p>
          </div>
          <Button 
            onClick={() => navigate('/post-job')}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={jobsRemaining === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Post New Job
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Active Jobs</p>
                    <p className="text-2xl font-bold text-slate-900">{activeCount}</p>
                  </div>
                  <Briefcase className="w-10 h-10 text-green-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Closed Jobs</p>
                    <p className="text-2xl font-bold text-slate-900">{closedCount}</p>
                  </div>
                  <Pause className="w-10 h-10 text-orange-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Jobs Remaining</p>
                    <p className="text-2xl font-bold text-slate-900">{jobsRemaining}</p>
                  </div>
                  <Plus className="w-10 h-10 text-blue-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Applications</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {jobs.reduce((sum, job) => sum + (job.applications || 0), 0)}
                    </p>
                  </div>
                <Users className="w-10 h-10 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Jobs List */}
        {jobs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Jobs Posted Yet</h3>
              <p className="text-slate-600 mb-6">
                Start posting jobs to attract top talent to your organization
              </p>
              <Button 
                onClick={() => navigate('/post-job')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post Your First Job
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map(job => (
              <Card key={job.job_id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-slate-900">{job.title}</h3>
                        {getStatusBadge(job.status)}
                      </div>
                      
                      <p className="text-lg text-slate-700 mb-3">{job.company_name}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.city ? `${job.city}, ` : ''}{job.country}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.job_type?.replace('_', '-')}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Posted {formatDate(job.published_at || job.created_at)}
                        </div>
                        
                        {job.salary_min && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {job.salary_currency} {job.salary_min.toLocaleString()} - {job.salary_max?.toLocaleString()}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-slate-500" />
                          <span>{job.views || 0} views</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-500" />
                          <span>{job.applications || 0} applications</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                        variant="outline"
                        size="sm"
                        title="View Job"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        onClick={() => navigate(`/jobs/${job.job_id}/applicants`)}
                        variant="outline"
                        size="sm"
                        title="View Applicants"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        {job.applications || 0}
                      </Button>
                      
                      <Button
                        onClick={() => navigate(`/jobs/${job.job_id}/analytics`)}
                        variant="outline"
                        size="sm"
                        title="View Analytics"
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        onClick={() => handleViewMiReport(job.job_id, job.mi_report_status)}
                        variant="outline"
                        size="sm"
                        title={
                          job.mi_report_status === 'completed' ? 'View Market Intelligence' :
                          job.mi_report_status === 'generating' || job.mi_report_status === 'pending' ? 'Report Generating...' :
                          'Generate Market Intelligence'
                        }
                        className="border-teal-300 text-teal-600 hover:bg-teal-50 relative"
                        disabled={loadingMi}
                      >
                        {job.mi_report_status === 'generating' || job.mi_report_status === 'pending' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Lightbulb className="w-4 h-4" />
                        )}
                        {job.mi_report_status === 'completed' && (
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                      </Button>
                      
                      <Button
                        onClick={() => handleSmartSourcing(job.job_id, job.title)}
                        variant="outline"
                        size="sm"
                        title="Find Matching Candidates"
                        className="border-indigo-300 text-indigo-600 hover:bg-indigo-50 relative"
                        disabled={loadingSourcing}
                      >
                        {loadingSourcing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Target className="w-4 h-4" />
                        )}
                        <Sparkles className="w-3 h-3 absolute -top-0.5 -right-0.5 text-yellow-500" />
                      </Button>
                      
                      <Button
                        onClick={() => window.open(`/discovery/${job.job_id}`, '_blank')}
                        variant="outline"
                        size="sm"
                        title="Discovery Call Questions"
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      >
                        <MessageCircleQuestion className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        onClick={() => navigate(`/post-job/${job.job_id}`)}
                        variant="outline"
                        size="sm"
                        title="Edit Job"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      {job.status === 'active' && (
                        <Button
                          onClick={() => handlePause(job.job_id)}
                          variant="outline"
                          size="sm"
                          title="Pause Job"
                        >
                          <Pause className="w-4 h-4" />
                        </Button>
                      )}
                      
                      <Button
                        onClick={() => handleDelete(job.job_id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        title="Delete Job"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* MI Report Modal */}
      {showMiModal && miReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lightbulb className="w-6 h-6 text-teal-600" />
                <h2 className="text-2xl font-bold text-slate-900">Market Intelligence Report</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => window.open(`${BACKEND_URL}/api/reports/${miReport.report_id}/pdf`, '_blank')}
                  size="sm"
                  variant="outline"
                  className="border-teal-600 text-teal-600 hover:bg-teal-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => setShowMiModal(false)}
                  size="sm"
                  variant="outline"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Role Overview */}
              {miReport.report.roleOverview && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Role Overview</h3>
                  <div className="space-y-2">
                    <p><strong>Job Title:</strong> {miReport.report.roleOverview.jobTitle}</p>
                    <p><strong>Type:</strong> {miReport.report.roleOverview.jobType}</p>
                    <p><strong>Location:</strong> {miReport.report.roleOverview.location}</p>
                    <p><strong>Experience:</strong> {miReport.report.roleOverview.experienceRequired}</p>
                    <p className="mt-2">{miReport.report.roleOverview.summary}</p>
                  </div>
                </div>
              )}

              {/* Compensation */}
              {miReport.report.compensation && miReport.report.compensation.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Compensation Insights</h3>
                  {miReport.report.compensation.map((comp, idx) => (
                    <div key={idx} className="bg-green-50 p-4 rounded-lg border border-green-200 mb-3">
                      <p><strong>{comp.type}:</strong> {comp.salaryRange}</p>
                      <p className="text-sm text-slate-600 mt-1">{comp.totalComp}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Talent Market */}
              {miReport.report.talentMarket && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Talent Market Analysis</h3>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <p><strong>Active Postings:</strong> {miReport.report.talentMarket.activePostings}</p>
                    <p><strong>Demand Level:</strong> {miReport.report.talentMarket.demandLevel}</p>
                    <p><strong>Supply Level:</strong> {miReport.report.talentMarket.supplyLevel}</p>
                    <p className="mt-2 text-sm">{miReport.report.talentMarket.insights}</p>
                  </div>
                </div>
              )}

              {/* Boolean Searches */}
              {miReport.report.booleanSearches && miReport.report.booleanSearches.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Sourcing Strategies</h3>
                  {miReport.report.booleanSearches.map((search, idx) => (
                    <div key={idx} className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-2">
                      <p className="font-semibold">{search.platform}</p>
                      <code className="text-sm bg-white p-2 rounded mt-2 block">{search.searchString}</code>
                      {search.searchUrl && (
                        <a href={search.searchUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm mt-2 inline-block">
                          Open Search →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Smart Sourcing Modal */}
      {showSourcingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-gradient-to-br from-white to-indigo-50 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-indigo-200">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Smart Sourcing Results</h2>
                  <p className="text-indigo-100 text-sm">{sourcingJobTitle} - {sourcingCandidates.length} Matches Found</p>
                </div>
              </div>
              <Button
                onClick={() => setShowSourcingModal(false)}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {sourcingCandidates.map((candidate, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 border-2 border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg text-white text-2xl font-bold">
                      {candidate.name.charAt(0)}
                    </div>
                    
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900 mb-1">{candidate.name}</h3>
                          {candidate.title && (
                            <p className="text-base text-slate-600 font-medium">{candidate.title}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 text-sm font-bold flex items-center gap-1">
                            <Star className="w-3 h-3 fill-white" />
                            {candidate.match_score}% Match
                          </Badge>
                        </div>
                      </div>

                      {/* Company & Location */}
                      {(candidate.company || candidate.location) && (
                        <div className="flex items-center gap-4 mb-3 text-sm text-slate-600">
                          {candidate.company && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {candidate.company}
                            </span>
                          )}
                          {candidate.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {candidate.location}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Match Reasons */}
                      {candidate.match_reasons && (
                        <div className="bg-indigo-50 rounded-lg p-3 mb-3 border border-indigo-200">
                          <p className="text-xs font-bold text-indigo-900 mb-2 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Why This Candidate Matches:
                          </p>
                          <ul className="space-y-1">
                            {candidate.match_reasons.slice(0, 3).map((reason, i) => (
                              <li key={i} className="text-xs text-indigo-800 flex items-start gap-2">
                                <Award className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{reason}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
                        {candidate.contact_info?.email && (
                          <div className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-cyan-50 px-3 py-2 rounded-lg border border-blue-200">
                            <Mail className="w-4 h-4 text-blue-600" />
                            <a href={`mailto:${candidate.contact_info.email}`} className="text-sm font-semibold text-blue-700 hover:underline">
                              {candidate.contact_info.email}
                            </a>
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              {candidate.contact_info.confidence}% Verified
                            </Badge>
                          </div>
                        )}
                        
                        {candidate.contact_info?.linkedin_url && (
                          <a 
                            href={candidate.contact_info.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-all text-sm font-semibold"
                          >
                            <Linkedin className="w-4 h-4" />
                            View LinkedIn
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        
                        {candidate.platform === 'github' && (
                          <a 
                            href={candidate.profile_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-gray-800 text-white px-3 py-2 rounded-lg hover:bg-gray-900 transition-all text-sm font-semibold"
                          >
                            <Github className="w-4 h-4" />
                            View GitHub
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        
                        {!candidate.contact_info?.email && !candidate.contact_info?.linkedin_url && (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Mail className="w-4 h-4" />
                            <span>Contact info not available - use profile link</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {sourcingCandidates.length === 0 && (
                <div className="text-center py-12">
                  <Target className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                  <p className="text-slate-600">No candidates found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyJobsPage;
